/**
 * KAN-866: чей это кабинет (название организации) и кто вошёл.
 * Видно на любой ширине: обе части обрезаются многоточием, название кабинета
 * берёт до 60% ширины, остальное имени.
 * compact: "Кабинет / Имя" мелким текстом без пометки "менеджер" (шапка /m).
 * as: тег корня (header в приложении менеджера), чтобы пустая обёртка не
 * рисовалась, пока пользователь не загружен.
 */
import { Building2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cabinetUserLabel } from '@/lib/cabinetIdentity';
import { cn } from '@/lib/utils';

interface CabinetIdentityProps {
  variant?: 'default' | 'compact';
  as?: 'div' | 'header';
  className?: string;
}

export function CabinetIdentity({
  variant = 'default',
  as: Tag = 'div',
  className,
}: CabinetIdentityProps) {
  const { data: user } = useCurrentUser();
  const orgName = user?.organization?.name;
  if (!user || !orgName) return null;

  const compact = variant === 'compact';
  const { name, isManager } = cabinetUserLabel(user);

  return (
    <Tag
      className={cn(
        'flex min-w-0 items-center',
        compact ? 'gap-1.5 text-xs text-gray-600' : 'gap-2 text-sm text-gray-900',
        className
      )}
      data-testid="cabinet-identity"
    >
      <span
        className="flex min-w-0 max-w-[60%] shrink-0 items-center gap-1.5 font-medium"
        title={orgName}
        data-testid="cabinet-identity-org"
      >
        <Building2
          className={cn('shrink-0 text-gray-500', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')}
          aria-hidden="true"
        />
        <span className="truncate">{orgName}</span>
      </span>
      {name && (
        <>
          <span className="shrink-0 text-gray-300" aria-hidden="true">
            /
          </span>
          <span
            className={cn(
              'flex min-w-0 flex-1 items-center gap-1.5',
              compact ? 'text-gray-500' : 'text-gray-600'
            )}
            title={user.email ?? name}
            data-testid="cabinet-identity-user"
          >
            <span className="truncate" data-testid="cabinet-identity-user-name">
              {name}
            </span>
            {!compact && isManager && (
              <span className="hidden shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 sm:inline">
                менеджер
              </span>
            )}
          </span>
        </>
      )}
    </Tag>
  );
}
